import React, { useEffect, useRef, useState } from 'react';
import { Loader2, ImageIcon, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ConceptIllustration
 *
 * Shows a friendly AI-generated illustration next to a lesson concept.
 * - First tries a cache lookup (cheap GET).
 * - If not cached, kicks off the (heavier) POST generate call.
 * - Same concept text on the same subject reuses the same image everywhere.
 */
export default function ConceptIllustration({ concept, explanation = '', subject = 'general' }) {
  const { api } = useAuth();
  const [imageUrl, setImageUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  // Keep a stable ref to the axios instance — AuthContext recreates `api` on every render,
  // so relying on it as a dependency would cancel our in-flight requests before they finish.
  const apiRef = useRef(api);
  useEffect(() => { apiRef.current = api; }, [api]);

  useEffect(() => {
    if (!concept) return undefined;

    let cancelled = false;

    (async () => {
      setStatus('loading');
      setImageUrl(null);

      try {
        const client = apiRef.current;
        // 1) Fast cache lookup
        const params = new URLSearchParams({ concept, subject, explanation: explanation.slice(0, 500) });
        const lookupRes = await client.get(`/concept-image/lookup?${params.toString()}`).catch(() => null);
        if (lookupRes && lookupRes.data && lookupRes.data.image_url) {
          if (cancelled) return;
          setImageUrl(lookupRes.data.image_url);
          setStatus('ready');
          return;
        }

        // 2) Not cached — generate now (slower). Backend dedupes via cache_key so parallel
        // requests from StrictMode double-invoke are safe. Use a generous timeout since
        // gpt-image-1 can take 15-45s. Only paid once per unique concept.
        const genRes = await apiRef.current.post(
          '/concept-image/generate',
          { concept, explanation: explanation.slice(0, 600), subject },
          { timeout: 90000 },
        );
        if (cancelled) return;
        if (genRes && genRes.data && genRes.data.image_url) {
          setImageUrl(genRes.data.image_url);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
    // Intentionally NOT depending on `api` — it's unstable across renders.
  }, [concept, explanation, subject]);

  // Resolve the image URL against REACT_APP_BACKEND_URL when it's a relative /api path.
  const resolvedSrc = (() => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}${imageUrl}`;
  })();

  if (status === 'loading') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 flex flex-col items-center justify-center min-h-[220px]" data-testid="concept-illustration-loading">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
          <Loader2 className="w-7 h-7 text-emerald-500 animate-spin" />
        </div>
        <p className="text-sm text-slate-500 font-medium">Drawing a picture…</p>
        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Zippie is illustrating this lesson
        </p>
      </div>
    );
  }

  if (status === 'ready' && resolvedSrc) {
    return (
      <div className="rounded-2xl overflow-hidden border border-emerald-100 bg-white shadow-sm" data-testid="concept-illustration">
        <img
          src={resolvedSrc}
          alt={`Illustration: ${concept}`}
          className="w-full h-auto max-h-80 object-contain bg-gradient-to-br from-emerald-50 to-white"
          loading="lazy"
        />
      </div>
    );
  }

  // Silent when errored — the concept card already has explanation text; a broken image UI is worse than none.
  if (status === 'error') {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-4 flex items-center gap-3 text-slate-400" data-testid="concept-illustration-fallback">
        <ImageIcon className="w-5 h-5" />
        <p className="text-xs">Picture unavailable right now.</p>
      </div>
    );
  }

  return null;
}
